import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Apple, Users, HeartHandshake, HelpCircle, User } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

const BottomNav = () => {
  const location = useLocation();
  const { t } = useLanguage();
  
  // Hide bottom nav on auth-related + marketing pages
  const authPages = [
    '/login',
    '/register',
    '/forgot-password',
    '/reset-password',
    '/landing',
    '/onboarding',
    '/how-it-works',
    '/vendor-login',
    '/vendor-register'
  ];

  // Also hide on the root landing page
  const shouldHideNav = location.pathname === '/' || authPages.some(page => location.pathname.startsWith(page));
  
  // Don't render bottom nav on auth pages
  if (shouldHideNav) {
    return null;
  }
  
  const navItems = [
    { path: '/dashboard',     labelKey: 'navShort.dashboard', fallbackKey: 'nav.dashboard', icon: Home },
    { path: '/nutrition',     labelKey: 'navShort.nutrition', fallbackKey: 'nav.nutrition', icon: Apple },
    { path: '/family-health', labelKey: 'navShort.family',    fallbackKey: 'nav.family',    icon: Users },
    { path: '/care-services', labelKey: 'navShort.care',      fallbackKey: 'nav.care',      icon: HeartHandshake },
    { path: '/support-help',  labelKey: 'navShort.support',   fallbackKey: 'nav.support',   icon: HelpCircle },
    { path: '/profile',       labelKey: 'navShort.profile',   fallbackKey: 'nav.profile',   icon: User }
  ];

  const isActive = (path) => {
    return location.pathname === path;
  };

  // Resolve label: prefer short key, fall back to standard nav key. The
  // t() helper returns the English fallback if neither is set in the
  // current locale — keeps mixed-language UI impossible.
  const resolveLabel = (item) => {
    const short = t(item.labelKey);
    if (short && short.length > 0) return short;
    return t(item.fallbackKey);
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-bottom-nav md:hidden">
      <div className="grid grid-cols-6 min-h-16">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);

          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex flex-col items-center justify-center gap-0.5 py-1.5 transition-colors ${
                active
                  ? 'text-healthloop-mint-600 bg-healthloop-mint-50'
                  : 'text-gray-600 hover:text-healthloop-mint-600 hover:bg-gray-50'
              }`}
            >
              <Icon className={`h-5 w-5 shrink-0 ${active ? 'scale-110' : ''}`} />
              {/* Label: 2-line wrap allowed (no truncate), smaller font on
                  narrow widths, hyphenate to break long compound words
                  cleanly (e.g. "Ìrànlọ́wọ́" in Yorùbá). */}
              <span
                className="text-[9px] min-[380px]:text-[10px] font-medium leading-[1.1] px-0.5 text-center break-words hyphens-auto max-w-full"
                style={{ wordBreak: 'break-word' }}
              >
                {resolveLabel(item)}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

export default BottomNav;
